import { buildProperty, buildCollection, EntityReference } from "@firecms/core";
import enumLocales from "./locales.enum";
import uniqid from "uniqid";

interface ICourse {
  custom_id: string;
  hidden: boolean;
  // category: string;
  price: number;
  active: boolean;
  show_whatsapp: boolean;
  show_pluralo: boolean;
  image: string;
  images: Array<string>;
  bar: string;
  map_image: string;
  color: string;
  video: string;
  pluralo_id: string;
  priority: number;
  category_reference: EntityReference;
}

const itinerarySectionRowProperties = {
  images: {
    name: "Images",
    dataType: "array" as const,
    of: {
      dataType: "reference" as const,
      path: "icons",
      description: "Reference to icon",
    },
  },
  text: {
    name: "Text",
    dataType: "string" as const,
    columnWidth: 400,
    markdown: true,
  },
};

const itinerarySectionItemProperties = {
  title: {
    name: "Title",
    dataType: "string" as const,
  },
  rows: {
    name: "Content rows",
    description:
      "Each row shows icons then text on one line. Add a new row for a line break.",
    dataType: "array" as const,
    columnWidth: 400,
    of: {
      dataType: "map" as const,
      properties: itinerarySectionRowProperties,
    },
  },
};

const CourseLocaleSchema = buildCollection({
  id: "course_locales",
  name: "Locale",
  path: "locales",
  customId: enumLocales,
  properties: {
    meta_description: buildProperty({
      name: "Meta description",
      dataType: "string",
      columnWidth: 400,
      markdown: true,
    }),
    meta_tags: buildProperty({
      name: "Meta description",
      dataType: "array",
      columnWidth: 400,
      of: {
        dataType: "string",
      },
    }),
    title: buildProperty({
      name: "Title",
      dataType: "string",
      columnWidth: 400,
    }),
    info: buildProperty({
      name: "Info",
      description:
        "Example of a complex array with multiple properties as children",
      validation: { required: false },
      dataType: "array",
      columnWidth: 400,
      oneOf: {
        properties: {
          icon: {
            dataType: "reference",
            name: "Icon",
            path: "icons",
            description: "Reference to icon",
            validation: {
              required: true,
            },
          },
          hyperlink: {
            dataType: "map",
            name: "Hyperlink",
            description: "Hyperlink",
            properties: {
              label: {
                name: "Label",
                dataType: "string",
              },
              url: {
                name: "URL",
                dataType: "string",
              },
            },
          },
          text: {
            dataType: "string",
            name: "Text",
          },
          small_text: {
            dataType: "string",
            name: "Small text",
          },
        },
      },
    }),
    main_description: buildProperty({
      name: "Descrição principal",
      dataType: "map",
      columnWidth: 400,
      properties: {
        label: {
          name: "Titulo",
          dataType: "string",
        },
        text: {
          name: "Texto",
          dataType: "string",
          columnWidth: 400,
          markdown: true,
        },
      },
    }),
    map_view: buildProperty({
      name: "Map view sections",
      dataType: "array",
      columnWidth: 400,
      of: {
        dataType: "map",
        properties: itinerarySectionItemProperties,
      },
    }),
    full_itinerary: buildProperty({
      name: "Full itinerary sections",
      dataType: "array",
      columnWidth: 400,
      of: {
        dataType: "map",
        properties: itinerarySectionItemProperties,
      },
    }),
    collapsed_section: buildProperty({
      name: "Collapsed section",
      dataType: "map",
      columnWidth: 400,
      properties: {
        rows: {
          name: "Rows",
          description:
            "Each row is a collapsible: a plain title (header) and a markdown body.",
          dataType: "array",
          columnWidth: 400,
          of: {
            dataType: "map",
            properties: {
              title: {
                name: "Title",
                dataType: "string",
              },
              text: {
                name: "Text",
                dataType: "string",
                columnWidth: 400,
                markdown: true,
              },
            },
          },
        },
      },
    }),
    // free_text: buildProperty({
    //   name: "Free",
    //   dataType: "array",
    //   oneOf: {
    //     typeField: "type",
    //     valueField: "value",
    //     properties: {
    //       images: {
    //         dataType: "string",
    //         name: "Image",
    //         storage: {
    //           storagePath: "images",
    //           acceptedFiles: ["image/*"]
    //         }
    //       },
    //       text: {
    //         dataType: "string",
    //         name: "Text",
    //         markdown: true
    //       },
    //       products: {
    //         name: "Products",
    //         dataType: "array",
    //         of: {
    //           dataType: "reference",
    //           path: "products",
    //           previewProperties: ["name", "main_image"]
    //         }
    //       }
    //     }
    //   }
    // })
  },
});

const CourseSchema = buildCollection<ICourse>({
  id: "courses",
  name: "Experiências",
  path: "courses",
  subcollections: [CourseLocaleSchema],
  properties: {
    custom_id: {
      name: "custom_id",
      validation: { required: true },
      dataType: "string",
    },
    hidden: {
      name: "hidden",
      dataType: "boolean",
      validation: { required: true },
      defaultValue: false,
    },
    // category: {
    //   name: "Category",
    //   validation: { required: true },
    //   dataType: "string",
    //   enumValues: {
    //     experiences: "Experiences",
    //     workshops: "Workshops",
    //     hikes: "Hikes",
    //     find_more: "Find more",
    //   },
    // },
    category_reference: {
      name: "Category",
      dataType: "reference",
      path: "categories",
      previewProperties: ["name"]
    },
    pluralo_id: {
      name: "pluralo_id",
      dataType: "string",
    },
    priority: {
      name: "Priority",
      dataType: "number",
      defaultValue: 0,
    },
    price: {
      name: "Price",
      dataType: "number",
      hideFromCollection: true,
    },
    active: {
      name: "Active",
      dataType: "boolean",
    },
    show_whatsapp: {
      name: "Show Whatsapp",
      dataType: "boolean",
      defaultValue: false,
    },
    show_pluralo: {
      name: "Show Pluralo",
      dataType: "boolean",
      defaultValue: false,
    },
    image: {
      name: "Hero image",
      dataType: "string",
      storage: {
        fileName: () => uniqid(),
        acceptedFiles: ["image/*"],
        storagePath: "images/courses",
        metadata: {
          cacheControl: "max-age=1000000",
        },
      },
    },
    bar: {
      name: "Bar image",
      dataType: "string",
      storage: {
        fileName: () => uniqid(),
        acceptedFiles: ["image/*"],
        storagePath: "images/courses",
        metadata: {
          cacheControl: "max-age=1000000",
        },
      },
    },
    map_image: {
      name: "Map image",
      dataType: "string",
      storage: {
        fileName: () => uniqid(),
        acceptedFiles: ["image/*"],
        storagePath: "images/courses",
        metadata: {
          cacheControl: "max-age=1000000",
        },
      },
    },
    images: {
      dataType: "array",
      name: "Images",
      of: {
        dataType: "string",
        storage: {
          fileName: () => uniqid(),
          acceptedFiles: ["image/*"],
          storagePath: "images/courses",
          metadata: {
            cacheControl: "max-age=1000000",
          },
        },
      },
      description: "This fields allows uploading multiple images at once",
    },
    color: {
      name: "color",
      validation: { required: true },
      dataType: "string",
      enumValues: {
        brown: "Brown",
        yellow: "Yellow",
        "yellow-darker": "Yellow Darker",
        blue: "Blue",
        green: "green",
      },
    },
    video: {
      name: "Video",
      dataType: "string",
      storage: {
        fileName: () => uniqid(),
        storagePath: "videos/courses",
        acceptedFiles: ["video/*"],
        metadata: {
          cacheControl: "max-age=1000000",
        },
      },
    },
  },
});

export default CourseSchema;
export { CourseLocaleSchema };
